import { Blocks, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { adminClient, type AdminIntegrationVisibilityDetail, type AdminOrganizationRow } from '@/api/adminClient';
import { IntegrationVisibilityEditor } from '@/components/integrations/IntegrationVisibilityControl';
import { AdminBreadcrumbs } from '@/components/shell/AdminBreadcrumbs';
import { PAGE_CONTAINER_CLASS, PAGE_TITLE_CLASS } from '@/components/shell/AdminPageHeader';
import { AdminPageLoadError } from '@/components/shell/AdminPageLoadError';
import { Button } from '@/components/ui/button';
import { integrationKindLabel } from '@/lib/integrationVisibility';
import { cn } from '@/lib/utils';

import { errorMessage } from '../IntegrationBuilder/builderModel';
import { IntegrationLogo } from '../IntegrationBuilder/builderUi';

/** Studio Admin → Integrations → one integration: who can use it, and a connector's testing organizations. */
export function IntegrationDetailPage() {
  const { integrationId = '' } = useParams();
  const [detail, setDetail] = useState<AdminIntegrationVisibilityDetail>();
  const [organizations, setOrganizations] = useState<AdminOrganizationRow[]>([]);
  const [error, setError] = useState<string>();
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setDetail(undefined);
    setError(undefined);
    Promise.all([adminClient.getIntegrationVisibility(integrationId), adminClient.listOrganizations()])
      .then(([next, list]) => {
        if (cancelled) return;
        setDetail(next);
        setOrganizations(list.items);
      })
      .catch((failure: unknown) => {
        if (!cancelled) setError(errorMessage(failure));
      });
    return () => {
      cancelled = true;
    };
  }, [integrationId, reloadKey]);

  if (error) {
    return <AdminPageLoadError message={error} onRetry={() => setReloadKey((key) => key + 1)} backTo="/integrations" backLabel="Back to Integrations" />;
  }
  if (!detail) {
    return (
      <div className={cn(PAGE_CONTAINER_CLASS, 'flex items-center gap-2 text-sm text-muted-foreground')}>
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Loading integration
      </div>
    );
  }

  return (
    <div className={PAGE_CONTAINER_CLASS}>
      <AdminBreadcrumbs items={[{ label: 'Integrations', to: '/integrations' }, { label: detail.title }]} />
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-4">
          <IntegrationLogo src={detail.logoUrl} color={detail.brandColor ?? '#64748b'} title={detail.title} size="lg" />
          <div className="min-w-0">
            <h1 className={cn(PAGE_TITLE_CLASS, 'truncate')}>{detail.title}</h1>
            <div className="mt-1 font-mono text-xs text-muted-foreground">
              {detail.integrationId} · {integrationKindLabel(detail.kind)}
            </div>
          </div>
        </div>
        {detail.draftId ? (
          <Button variant="outline" size="sm" asChild>
            <Link to={`/integrations/builder/${encodeURIComponent(detail.draftId)}`}>
              <Blocks className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
              Open in Integration Builder
            </Link>
          </Button>
        ) : null}
      </div>
      <IntegrationVisibilityEditor detail={detail} organizations={organizations} onSaved={setDetail} />
    </div>
  );
}
